import { useEffect, useRef } from 'react';
import { useSignageStore, useWidgetMessage, type MessageTransform } from '@/store/signageStore';

/**
 * The widget-facing data API, replacing legacy `componentRegister(uuid, options, store, module)`.
 *
 * A widget calls this once with its stable id (from `ModuleLoader`, SYM-491), the topic from
 * its config options, and optionally the transform for its type
 * (`transformsByWidgetType[component.type]`). It registers the topic on mount and returns the
 * latest payload for that component — `undefined` until the first message arrives.
 *
 * ```ts
 * const data = useWidgetData<WeatherHour[]>(idComp, options.topic, weatherTransform);
 * ```
 *
 * ## Absolute topics
 *
 * `{ absolute: true }` says the topic is already a full broker topic and `baseTopic` must not
 * be prefixed. Only `FloorOccupancyWidget` needs it today — its data sits one level above
 * `baseTopic`, on the tenant-wide environment tree (docs/MIGRATION.md decision #26) — but
 * nothing about the mechanism is specific to that widget. Any widget whose config carries an
 * `absoluteTopic` option can opt in the same way.
 *
 * ## No unregister on unmount
 *
 * There is deliberately no cleanup here, and that is not an oversight. Widgets in this app
 * never unmount except on a full page reload: the config is immutable, there is no
 * navigation, and nothing conditionally renders a widget away. Unregistering would remove a
 * topic from `topicItems` — the same list the preventer ACL was provisioned from and the MQTT
 * client subscribed with — so a stray unmount/remount cycle could leave a display subscribed
 * to nothing, or holding an ACL for topics it no longer asks for. The legacy
 * `unregisterModule` action was an empty no-op for exactly this reason. Leave it empty.
 */
// `T` is a caller-supplied assertion of the payload shape, not an inference — see the note on
// `useWidgetMessage` in the store.
// eslint-disable-next-line @typescript-eslint/no-unnecessary-type-parameters
export function useWidgetData<T>(
  componentId: string,
  topic: string | undefined,
  transform?: MessageTransform,
  { absolute = false }: { absolute?: boolean } = {},
): T | undefined {
  const registered = useRef(false);

  useEffect(() => {
    // StrictMode double-invokes effects in dev (and the ref survives the remount), so the
    // guard is what makes registration exactly-once. Registration is idempotent in the store
    // as well, but a second call would log a spurious duplicate-topic warning.
    if (registered.current) return;

    // Static widgets — DateWidget, Logo, ImageLabelWidget, ProgressBar — have no topic. The
    // legacy code had no guard and happily registered `${baseTopic}/undefined` for each of
    // them, which then went into the preventer ACL request as a junk topic the broker was
    // asked to grant read access to. Register nothing instead.
    if (!topic) return;

    registered.current = true;
    useSignageStore.getState().registerTopic({ componentId, topic, absolute, transform });
    // Every dependency is a primitive, so the default `{}` for the options argument cannot
    // retrigger this on a re-render the way an object identity would.
  }, [componentId, topic, absolute, transform]);

  // Selected unconditionally — hooks cannot be called conditionally, and a topicless widget
  // has no producer for `messages[componentId]`, so this is `undefined` for its lifetime.
  return useWidgetMessage<T>(componentId);
}
